import { Stack, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { PbjModelUpsertBody, PbjPrediction } from "../../../utils/models";
import { WAGE_OVERRIDE_FIELDS } from "./recommendations.constants";

type Props = {
	open: boolean;
	onClose: () => void;
	prediction: PbjPrediction;
	draft: PbjModelUpsertBody | null;
	// Keeps the parent's dirty draft in sync with the pending overrides.
	onDraftChange: (next: PbjModelUpsertBody) => void;
	// Persist + recompute so new wages flow into the recommendation math in one click.
	onCalculate: (next: PbjModelUpsertBody) => void;
};

// A blank field is legal — it clears the override so the computed wage is used. A
// non-blank value must be a finite, non-negative dollar amount; anything else
// surfaces as an explicit error and blocks confirm rather than being silently
// dropped (the old `Number.isFinite && >= 0` short-circuit that coerced to null).
const isWageValid = (raw: string): boolean => {
	const trimmed = raw.trim();
	if (trimmed === "") return true;
	const n = Number(trimmed);
	return Number.isFinite(n) && n >= 0;
};

export const WageOverridesDialog: FC<Props> = ({ open, onClose, prediction, draft, onDraftChange, onCalculate }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();

	// Local string-per-field state so a cleared field reads as "" → null on save.
	const [tempWages, setTempWages] = useState<Record<string, string>>({});

	// Seed each field from the saved/pending override if present, else leave blank so the
	// computed dept wage shows as the placeholder. Re-seed each time the dialog opens.
	// biome-ignore lint/correctness/useExhaustiveDependencies: seed once per open, not on every draft keystroke.
	useEffect(() => {
		if (!open) return;
		const seeded: Record<string, string> = {};
		for (const field of WAGE_OVERRIDE_FIELDS) {
			const override = draft ? draft[field.overrideKey] : null;
			seeded[field.overrideKey] = override === null || override === undefined ? "" : String(override);
		}
		setTempWages(seeded);
	}, [open]);

	const allValid = WAGE_OVERRIDE_FIELDS.every((field) => isWageValid(tempWages[field.overrideKey] ?? ""));

	const handleConfirm = () => {
		if (!draft) {
			onClose();
			return;
		}
		const next: PbjModelUpsertBody = { ...draft };
		let overriddenCount = 0;
		for (const field of WAGE_OVERRIDE_FIELDS) {
			const raw = (tempWages[field.overrideKey] ?? "").trim();
			// Confirm is blocked while invalid, so a non-blank value is already valid here.
			const parsed: number | null = raw === "" ? null : Number(raw);
			next[field.overrideKey] = parsed;
			if (parsed !== null) overriddenCount += 1;
		}
		track("pbj_model_wage_override_saved", {
			department_id: prediction.config.departmentId,
			fields_overridden: overriddenCount,
		});
		onDraftChange(next);
		onCalculate(next);
		onClose();
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			dataTestId="pbj-model-wage-overrides-dialog"
			title={t("pbj_model.recommendations.wage_overrides.title")}
			confirm={t("save")}
			cancel={t("close")}
			confirmButtonVariant="primary"
			confirmDisabled={!draft || !allValid}
			onConfirm={handleConfirm}
			body={
				<Stack spacing={2} sx={{ pt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						{t("pbj_model.recommendations.wage_overrides.help")}
					</Typography>
					{WAGE_OVERRIDE_FIELDS.map((field) => {
						const computed = prediction.deptWages[field.computedKey];
						return (
							<SystemValidationTextField
								key={field.overrideKey}
								fullWidth
								inputType="number"
								label={t(field.labelKey)}
								value={tempWages[field.overrideKey] ?? ""}
								onChange={(e) => setTempWages((prev) => ({ ...prev, [field.overrideKey]: e.target.value }))}
								placeholder={computed === null ? "" : computed.toFixed(2)}
								validation={{
									validationCallback: (v: string) => isWageValid(v),
									helperText: { invalid: t("pbj_model.recommendations.wage_overrides.invalid") },
									performValidation: "onlyAfterInitialChange",
									showValidWhenValid: "never",
								}}
								// Surface the computed wage as an always-visible hint (the field's placeholder
								// only shows on focus), so an empty field reads as "leaving blank uses this".
								defaultHelperText={computed === null ? "" : t("pbj_model.recommendations.wage_overrides.computed_hint", { value: computed.toFixed(2) })}
								inputProps={{ inputProps: { min: 0, step: "any", "data-testid": `pbj-model-wage-override-input-${field.testIdSuffix}` } }}
							/>
						);
					})}
				</Stack>
			}
		/>
	);
};
